import React from "react";
import { array, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { CRMSpacingRow } from "../../CRMSpacingRow/CRMSpacingRow";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMRadioTab } from "../../CRMRadioTab/CRMRadioTab";
import { useSelectedTab } from "../../../hooks/useSelectedTab";
import { ICRMPopoutOption } from "../../Simple/CRMPopoutOptions/CRMPopoutOptions";
import { TIcon } from "../../CRMIcon/CRMIcon";
import { CRMPartyGroupHeading } from "../../CRMPartyGroupHeading/CRMPartyGroupHeading";
import { TUserCaseUserTypePartyGroupTone } from "../../../../../domain/codecs/UserCaseUserType";

export type TCRMVerticalTabGroup = {
    label: string;
    tone: TUserCaseUserTypePartyGroupTone;
};

export type TCRMVerticalTab = {
    tabLabel: string;
    labelIcon?: TIcon;
    tabGroup?: TCRMVerticalTabGroup;
    tabOptions: Array<ICRMPopoutOption>;
    content: JSX.Element;
};

type TTabWithIndex = {
    tab: TCRMVerticalTab;
    index: number;
};

type TTabGrouping = {
    group: TCRMVerticalTabGroup | undefined;
    tabs: Array<TTabWithIndex>;
};

const groupConsecutiveTabs = (tabs: Array<TCRMVerticalTab>): Array<TTabGrouping> =>
    pipe(
        tabs,
        array.mapWithIndex((index, tab): TTabWithIndex => ({ tab, index })),
        array.reduce<TTabWithIndex, Array<TTabGrouping>>([], (groupings, tabWithIndex) => {
            const lastGrouping = array.last(groupings);
            const label = tabWithIndex.tab.tabGroup?.label;

            return option.isSome(lastGrouping) && lastGrouping.value.group?.label === label
                ? pipe(
                    groupings,
                    array.dropRight(1),
                    array.append({
                        ...lastGrouping.value,
                        tabs: [...lastGrouping.value.tabs, tabWithIndex],
                    }),
                )
                : pipe(
                    groupings,
                    array.append({
                        group: tabWithIndex.tab.tabGroup,
                        tabs: [tabWithIndex],
                    }),
                );
        }),
    );

export const CRMVerticalTabs = (props: {
    tabs: Array<TCRMVerticalTab>
    afterTabsElement?: JSX.Element;
    noTabsContent?: JSX.Element;
}): JSX.Element => {

    const {
        isSelectedTabIndex,
        setSelectedTabIndex,
        selectedTabIndex
    } = useSelectedTab<TCRMVerticalTab>(props.tabs);

    return (
        <CRMSpacingRow
            spacing="large"
            alignItems="flex-start"
            childSize={props.tabs.length === 0 ? "1fr 1fr" : "2fr 1fr"}
        >
            <>
                {/* NO TABS CONTENT */}
                {props.tabs.length === 0 && props.noTabsContent}
                {/* TABS CONTENT */}
                {props.tabs.length > 0 && selectedTabIndex <= props.tabs.length -1 && props.tabs[selectedTabIndex].content}
            </>
            {/* TABS */}
            <SpacingColumn spacing={CRMSpacing.LARGE}>
                {pipe(
                    groupConsecutiveTabs(props.tabs),
                    array.mapWithIndex((groupingIndex, grouping) =>
                        <SpacingColumn key={groupingIndex} spacing={CRMSpacing.SMALL}>
                            {grouping.group &&
                                <CRMPartyGroupHeading
                                    label={grouping.group.label}
                                    tone={grouping.group.tone}
                                    count={grouping.tabs.length}
                                />
                            }

                            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                {grouping.tabs.map(({ tab, index }) => (
                                    <CRMRadioTab
                                        key={index}
                                        isSelected={isSelectedTabIndex(index)}
                                        label={tab.tabLabel}
                                        onClick={() => setSelectedTabIndex(index)}
                                        icon={tab.labelIcon}
                                        options={tab.tabOptions}
                                    />
                                ))}
                            </SpacingColumn>
                        </SpacingColumn>
                    ),
                )}
                {props.afterTabsElement}
            </SpacingColumn>
        </CRMSpacingRow>
    );
};
